import React, { useState, useEffect } from 'react';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  Stack,
} from '@mui/joy';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';

const RenameModal = ({ open, onClose, onRename, item, loading }) => {
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  // Initialize name when item changes
  useEffect(() => {
    if (item) {
      setName(item.name);
      setError('');
    }
  }, [item]);

  const validateName = (value) => {
    if (!value.trim()) {
      return 'Name is required';
    }
    if (value.includes('/') || value.includes('\\')) {
      return 'Name cannot contain slashes';
    }
    if (value.length > 255) {
      return 'Name must be less than 255 characters';
    }
    return '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationError = validateName(name);
    if (validationError) {
      setError(validationError);
      return;
    }
    if (name.trim() === item?.name) {
      onClose();
      return;
    }
    onRename(name.trim());
  };

  const handleChange = (e) => {
    setName(e.target.value);
    if (error) {
      setError(validateName(e.target.value));
    }
  };

  if (!item) return null;

  const isFolder = item.type === 'folder';

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        variant="outlined"
        role="dialog"
        aria-labelledby="rename-modal-title"
        sx={{ minWidth: 400 }}
      >
        <DialogTitle id="rename-modal-title">
          <Stack direction="row" alignItems="center" gap={1}>
            <DriveFileRenameOutlineIcon />
            Rename {isFolder ? 'Folder' : 'File'}
          </Stack>
        </DialogTitle>
        <Divider />
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <FormControl error={!!error}>
              <FormLabel>New name</FormLabel>
              <Input
                autoFocus
                value={name}
                onChange={handleChange}
                placeholder={`Enter ${isFolder ? 'folder' : 'file'} name`}
                disabled={loading}
                onFocus={(e) => {
                  // Select filename without extension for files
                  if (!isFolder) {
                    const dotIndex = name.lastIndexOf('.');
                    if (dotIndex > 0) {
                      e.target.setSelectionRange(0, dotIndex);
                    } else {
                      e.target.select();
                    }
                  } else {
                    e.target.select();
                  }
                }}
              />
              {error && <FormHelperText>{error}</FormHelperText>}
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button
              variant="solid"
              color="primary"
              type="submit"
              loading={loading}
              disabled={!name.trim() || !!error}
            >
              Rename
            </Button>
            <Button variant="plain" color="neutral" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
          </DialogActions>
        </form>
      </ModalDialog>
    </Modal>
  );
};

export default RenameModal;
